/**
 * Pure model for the structured `call_qc` expected-output editor.
 *
 * A `call_qc` trace output looks like:
 *   { findings: Finding[], structured_findings: Finding[], ...rest }
 * where the two arrays are PARALLEL (same order, same metric_keys — including
 * any duplicate metric_key). A `Finding` is `{ metric_key, raw_score, evidence }`;
 * `raw_score` is the "answer" (binary 1/0, or 1/0.5/0 for scale questions).
 *
 * The editor lets the reviewer pick which findings to include and change each
 * answer, then rebuilds a valid `{ findings, structured_findings }` object with
 * both arrays kept in sync. All functions here are pure and unit-tested.
 */

export interface QcFinding {
  metric_key?: string;
  raw_score?: number;
  [key: string]: unknown;
}

export interface QcCallOutput {
  findings?: QcFinding[];
  structured_findings?: QcFinding[];
  [key: string]: unknown;
}

/** Per-pool-position editor state: included in the output, and the chosen answer. */
export interface FindingSelection {
  included: boolean;
  rawScore: number | undefined;
}

/**
 * Parse a call_qc output. Returns null when it isn't a structured findings
 * output (so the caller can fall back to the raw JSON editor).
 */
export function parseQcOutput(value: string | unknown): QcCallOutput | null {
  let obj: unknown;
  if (typeof value === 'string') {
    if (!value.trim()) return null;
    try {
      obj = JSON.parse(value);
    } catch {
      return null;
    }
  } else {
    obj = value;
  }
  if (obj && typeof obj === 'object' && Array.isArray((obj as QcCallOutput).findings)) {
    return obj as QcCallOutput;
  }
  return null;
}

/** True if this output can be edited with the structured findings UI. */
export function isStructuredQcOutput(value: string | unknown): boolean {
  return parseQcOutput(value) !== null;
}

/**
 * Derive per-position selection state by matching the current value's findings
 * onto the pool (the trace's full finding list) by metric_key, occurrence-order.
 * Positions present in `value` are included (with the value's answer); the rest
 * are excluded (defaulting to the pool's answer).
 */
export function deriveSelections(pool: QcCallOutput, value: QcCallOutput): FindingSelection[] {
  const poolFindings = pool.findings ?? [];
  const valueFindings = value.findings ?? [];
  const used = new Array<boolean>(poolFindings.length).fill(false);
  const selections: FindingSelection[] = poolFindings.map(f => ({
    included: false,
    rawScore: f.raw_score,
  }));

  for (const vf of valueFindings) {
    const idx = poolFindings.findIndex((pf, i) => !used[i] && pf.metric_key === vf.metric_key);
    if (idx >= 0) {
      used[idx] = true;
      selections[idx] = { included: true, rawScore: vf.raw_score };
    }
  }
  return selections;
}

/**
 * Rebuild the expected-output object from the pool + selection state, in pool
 * order, keeping `findings` and `structured_findings` in sync (same positions,
 * same edited `raw_score`). Preserves any other top-level keys from the pool.
 */
export function serializeQcOutput(
  pool: QcCallOutput,
  selections: FindingSelection[],
): QcCallOutput {
  const poolFindings = pool.findings ?? [];
  const poolStructured = pool.structured_findings ?? [];
  const findings: QcFinding[] = [];
  const structuredFindings: QcFinding[] = [];

  selections.forEach((sel, i) => {
    if (!sel.included) return;
    findings.push({ ...poolFindings[i], raw_score: sel.rawScore });
    if (poolStructured[i] !== undefined) {
      structuredFindings.push({ ...poolStructured[i], raw_score: sel.rawScore });
    }
  });

  const next: QcCallOutput = { ...pool, findings };
  // Only emit structured_findings if the source had them.
  if (pool.structured_findings !== undefined) {
    next.structured_findings = structuredFindings;
  }
  return next;
}
